/**
 * "Show me" walks. A walk is the lesson's own steps, shown one card at a time
 * over the REAL page. Each step lists the real element(s) it points at
 * (`data-tour="…"` attributes on the pages; the shell's own labels for the top
 * bar and sidebar). A step whose element is not on the page shows its card
 * with no ring.
 *
 * A walk only points. It never types, never picks, and presses nothing except
 * the controls in TOUR_OPENERS — buttons that open a form so its fields can be
 * shown. No walk presses a control that saves or sends.
 *
 * A lesson has a "Show me" button only when it is listed here (the catalog's
 * flag means "allowed", this list means "available").
 */
import { canOpenProfileRoute } from '@/lib/profileAccess';
import type { ProfileUser } from '@/lib/profileIdentity';
import { canShowMe, type TourLesson, type TourPlatform } from './tourCatalog';
import type { TourWalk } from './tourState';

export interface WalkStepTarget {
  /** Real element(s) to keep bright. Several selectors → one ring around all of them. */
  selector?: string;
  /** Page the step runs on. Missing → the page the walk is already on. */
  route?: string;
  /** Opener (from TOUR_OPENERS) pressed when the step's element is not on the page yet. */
  open?: string;
  /** The element is only there in some cases (a filter is set, a contact is picked, a narrow screen). */
  optional?: boolean;
}

const at = (...names: string[]) => names.map((name) => `[data-tour="${name}"]`).join(', ');
const SIDEBAR = '[data-aura-chrome="sidebar"]';
/** The sidebar row of a page (the href is "#/…" in the hash-routed preview). */
const sidebarLink = (to: string) => `${SIDEBAR} a[href="${to}"], ${SIDEBAR} a[href="#${to}"]`;
const ACCOUNT_MENU = at('account-menu');
const NEW_TICKET = at('support-new-ticket');

/** The only controls a walk may press. Each one opens a form (pressing it twice changes nothing); none saves or sends. */
export const TOUR_OPENERS: readonly string[] = [NEW_TICKET];

const onPage = (route: string, steps: WalkStepTarget[]): WalkStepTarget[] => steps.map((step) => ({ ...step, route }));

export const TOUR_WALKS: Record<string, WalkStepTarget[]> = {
  // Find your way around the web app — every target is in the shell, so it runs over the lesson page.
  'M01-L06': [
    { selector: SIDEBAR },
    { selector: `${SIDEBAR} nav` },
    { selector: '[aria-label="Search contacts, leads, invoices"]' },
    { selector: '[aria-label="Notifications"]' },
    { selector: ACCOUNT_MENU },
    { selector: '[title="Dark mode"], [title="Light mode"], [title="Switch to compact"], [title="Switch to comfortable"]' },
    { selector: '[aria-label="Open navigation"]', optional: true },
  ],
  // Look up a lead on the web.
  'M03-L10': [
    { selector: sidebarLink('/leads') },
    ...onPage('/leads', [
      { selector: at('leads-search') },
      { selector: at('leads-stage-tabs') },
      { selector: at('leads-filters') },
      { selector: at('leads-clear'), optional: true },
      { selector: at('leads-view-toggle') },
    ]),
  ],
  // See your follow-ups on the web.
  'M06-L12': [
    { selector: sidebarLink('/follow-ups') },
    ...onPage('/follow-ups', [
      { selector: at('followups-count') },
      { selector: at('followups-list') },
      // Points at the first row's "✓ Mark done" — never presses it. Only a
      // dated row has one.
      { selector: `${at('followups-list')} > li:first-child [data-testid^="fu-done-"]`, optional: true },
      { selector: at('followups-refresh') },
    ]),
  ],
  // Quick Quote on the web: start and choose the customer.
  'M08-L17': [
    { selector: sidebarLink('/quick-quote') },
    ...onPage('/quick-quote', [
      { selector: at('qq-title', 'qq-contact', 'qq-account') },
      { selector: at('qq-buyer-state', 'qq-customer-type') },
      { selector: at('qq-contact-edit'), optional: true },
      { selector: at('qq-client-details'), optional: true },
    ]),
  ],
  // Report a bug from the web — stops before "Submit".
  'M14-L16': [
    { selector: ACCOUNT_MENU },
    ...onPage('/support/my', [
      { selector: NEW_TICKET },
      { selector: at('support-category'), open: NEW_TICKET },
      { selector: at('support-module', 'support-severity'), open: NEW_TICKET },
      { selector: at('support-subject', 'support-description'), open: NEW_TICKET },
      { selector: at('support-submit'), open: NEW_TICKET },
    ]),
  ],
  // Make a support ticket on the web and follow it — stops before "Submit".
  'M14-L17': [
    { selector: ACCOUNT_MENU },
    ...onPage('/support/my', [
      { selector: at('support-tickets') },
      { selector: NEW_TICKET },
      { selector: at('support-tickets') },
    ]),
  ],

  // ---- M22 Kiaan and page notes (2026-10-23). The walks only point. The Kiaan panel, the page-notes bar and the
  // Dev mode tab are opened by the person, so a step on them is `optional` (no ring while they are closed).
  // A step with no selector is an action in the page-notes bar that has no element of its own (card, no ring).
  // What Kiaan is.
  'M22-L01': [
    { selector: at('kiaan-widget') },
    { selector: at('kiaan-manager'), optional: true },
    { selector: at('kiaan-widget') },
    { selector: at('kiaan-live-work'), optional: true },
    { selector: at('kiaan-widget') },
  ],
  // Write a page note.
  'M22-L02': [
    { selector: at('kiaan-page-notes'), optional: true },
    { optional: true },
    { optional: true },
    { optional: true },
    { optional: true },
    { selector: at('kiaan-page-notes'), optional: true },
  ],
  // Send notes and follow them.
  'M22-L03': [
    { selector: at('pn-my-notes'), optional: true },
    { selector: at('pn-tabs'), optional: true },
    { selector: at('pn-send'), optional: true },
    { selector: at('pn-tabs'), optional: true },
    { selector: at('pn-bell'), optional: true },
    { selector: '[data-testid^="pn-verdict-"]', optional: true },
  ],
  // Ask Kiaan · Manager.
  'M22-L05': [
    { selector: at('kiaan-manager'), optional: true },
    { selector: at('kiaan-chat'), optional: true },
    { selector: at('kiaan-chat'), optional: true },
    { selector: at('kiaan-live-work'), optional: true },
    { selector: at('kiaan-chat'), optional: true },
    { selector: at('kiaan-chat'), optional: true },
  ],
  // Approve a design — stops before "Approve".
  'M22-L06': onPage('/mockupboard', [
    { selector: at('mb-tabs') },
    { selector: at('mb-cards'), optional: true },
    { selector: at('mb-cards'), optional: true },
    { selector: at('mb-cards'), optional: true },
    { selector: at('mb-tabs') },
  ]),
  // Test an app on the Test Phone.
  'M22-L07': onPage('/dev/test-phone', [
    { selector: at('tp-phone') },
    { selector: at('tp-apps') },
    { selector: at('tp-install') },
    { selector: at('tp-keys') },
    { selector: at('tp-who') },
    { selector: at('tp-phone') },
    { selector: at('tp-who') },
  ]),
  // Teach Kiaan Browser a job (Dev mode tab).
  'M22-L08': [
    { selector: at('kiaan-tabs'), optional: true },
    { selector: at('kb-screen'), optional: true },
    { selector: at('kb-teach'), optional: true },
    { selector: at('kb-teach'), optional: true },
    { selector: at('kb-teach'), optional: true },
    { selector: at('kb-jobs'), optional: true },
  ],
};

/**
 * True when this lesson has a walk this person can run here: the lesson allows
 * it, it is wired above, and every page it opens is in the person's own menu.
 */
export function hasWalk(lesson: TourLesson, platform: TourPlatform, user: ProfileUser | null | undefined): boolean {
  const targets = TOUR_WALKS[lesson.id];
  if (!targets || !canShowMe(lesson, platform)) return false;
  return targets.every((step) => !step.route || canOpenProfileRoute(user, step.route));
}

/** The control the step names ("Quick Quote"), else "Step n". */
function stepTitle(body: string, index: number): string {
  return body.match(/"([^"]+)"/)?.[1] ?? `Step ${index + 1}`;
}

export function walkFor(lesson: TourLesson): TourWalk {
  const targets = TOUR_WALKS[lesson.id] ?? [];
  const stop = lesson.showMe.stopBefore;
  // "Yes, stop before …": say so on the last step that names that button. The walk never presses it.
  const named = stop ? lesson.steps.map((body) => body.includes(stop)).lastIndexOf(true) : -1;
  const stopAt = !stop ? -1 : named >= 0 ? named : lesson.steps.length - 1;
  return {
    lessonId: lesson.id,
    lessonTitle: lesson.title,
    steps: lesson.steps.map((body, index) => {
      const target = targets[index];
      return {
        selector: target?.selector,
        route: target?.route,
        open: target?.open && TOUR_OPENERS.includes(target.open) ? target.open : undefined,
        title: stepTitle(body, index),
        body: index === stopAt ? `${body} The walk stops before ${stop}.` : body,
      };
    }),
  };
}
